import {
  normalizeRangeToGranularity,
  type DateRangeType,
  type PeriodGranularityType
} from '#domain/core/filter/period';
import dayjs, { type Dayjs } from 'dayjs';
import type { MonthModelType, PickerModelType } from '../types/rangeCalendars.type';

/**
 * Projects a range onto the model shape vue-datepicker expects for the current granularity.
 */
export function toPickerModel(range: DateRangeType | null, granularity: PeriodGranularityType): PickerModelType | null {
  if (!range) {
    return null;
  }

  const { start, end } = normalizeRangeToGranularity(range, granularity);

  switch (granularity) {
    case 'weekly':
      return [
        [start.toDate(), start.endOf('isoWeek').toDate()],
        [end.startOf('isoWeek').toDate(), end.toDate()]
      ];
    case 'monthly':
      return [
        { month: start.month(), year: start.year() },
        { month: end.month(), year: end.year() }
      ];
    default:
      return [start.toDate(), end.toDate()];
  }
}

/**
 * Reads back what vue-datepicker emits, or null while the selection is still incomplete.
 */
export function fromPickerModel(model: unknown, granularity: PeriodGranularityType): DateRangeType | null {
  // Range mode always hands back a two-slot tuple [start, end]; anything else is not a selection at all.
  if (!Array.isArray(model) || model.length !== 2 || !isCompleteSlot(model[0]) || !isCompleteSlot(model[1])) {
    return null;
  }

  const dates = collectDates(model).sort((a, b) => a.valueOf() - b.valueOf());
  const [first] = dates;
  const last = dates.at(-1);

  if (!first || !last) {
    return null;
  }

  return normalizeRangeToGranularity({ start: first, end: last }, granularity);
}

// A selection is incomplete when a slot is missing entirely (daily/monthPicker send `null`
// for the pending second click) or, for the weekPicker, when it is an empty array instead —
// the library's weekly `emitModelValue` builder leaves the second slot as `[]` until a second
// week is clicked, so a naive flat date count would treat a single clicked week as complete.
function isCompleteSlot(slot: unknown): boolean {
  if (slot === null || slot === undefined) {
    return false;
  }

  return !Array.isArray(slot) || slot.length > 0;
}

function collectDates(model: unknown): Dayjs[] {
  if (Array.isArray(model)) {
    return model.flatMap(collectDates);
  }

  if (model instanceof Date) {
    return [dayjs(model)];
  }

  if (isMonthModel(model)) {
    return [dayjs().date(1).year(model.year).month(model.month)];
  }

  return [];
}

function isMonthModel(value: unknown): value is MonthModelType {
  const candidate = value as Partial<MonthModelType> | null;

  return typeof candidate?.month === 'number' && typeof candidate.year === 'number';
}
